import React, { useState, useRef } from "react";
import { Grid, TextField, Paper, Typography, Button } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CodeIcon from "@material-ui/icons/Code";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { S3_CONFIG } from "../config/UPMSConfig";
import { IPatientDetails, INewRxStepHandoff } from "../types/PrescriptionIngestionTypes";
import { useS3Operations, useUPMSIngestion } from "../hooks";
import { COLORS } from "../styles";
import { WorkflowCard, WorkflowSelectionHeader } from "../components";
import {
  StyledHeaderBox,
  DataAccordion,
  StyledActionButton,
  CustomerCreationSection,
  ResetWorkflowButton,
} from "../components";

interface IProps {
  data?: any;
  // Context carried from the Upload step via Continue. When present, the S3
  // retrieval path is auto-selected with the fileName prefilled, and the
  // customer identifiers are carried forward to the Approve step.
  handoff?: INewRxStepHandoff;
  // Advances to the Approve step, carrying personId/profileId plus the looked-up
  // prescriptionId.
  onContinue?: (handoff: INewRxStepHandoff) => void;
  // Advances to the Route to Follow-Up step with the same handoff.
  onContinueToFollowUp?: (handoff: INewRxStepHandoff) => void;
}

enum WorkflowType {
  NONE = "NONE",
  S3_RETRIEVAL = "S3_RETRIEVAL",
  UPLOAD_JSON = "UPLOAD_JSON",
}

const IngestNewRxToUPMSSection: React.FC<IProps> = ({ handoff, onContinue, onContinueToFollowUp }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>(
    handoff?.fileName ? WorkflowType.S3_RETRIEVAL : WorkflowType.NONE
  );
  const [fileName, setFileName] = useState(handoff?.fileName || "");
  const [jsonInput, setJsonInput] = useState("");
  const [patientDetails, setPatientDetails] = useState<IPatientDetails | null>(
    null
  );

  const step2Ref = useRef<HTMLDivElement>(null);
  const nextStepRef = useRef<HTMLDivElement>(null);
  const workflowContentRef = useRef<HTMLDivElement>(null);

  // Use our custom hooks
  const {
    isRetrieving,
    retrievedData,
    error: s3Error,
    retrieveFromS3,
    reset: resetS3,
  } = useS3Operations();
  const {
    isIngesting,
    upmsResponse,
    transformedPayload,
    error: upmsError,
    ingestNewRx,
    reset: resetUPMS,
  } = useUPMSIngestion();

  const isLoading = isRetrieving || isIngesting;

  const extractPatientDetails = (data: any): IPatientDetails | null => {
    try {
      let prescriptionData = data;

      // Handle wrapped rxPayload
      if (data.rxPayload && typeof data.rxPayload === "string") {
        prescriptionData = JSON.parse(data.rxPayload);
      }

      const patient = prescriptionData.Patient;
      if (!patient) return null;

      return {
        firstName: patient.Name?.FirstName || "",
        lastName: patient.Name?.LastName || "",
        dateOfBirth: patient.DateOfBirth?.Date || "",
        gender: patient.Gender || "",
        addressLine1: patient.Address?.AddressLine1 || "",
        addressLine2: patient.Address?.AddressLine2 || "",
        city: patient.Address?.City || "",
        state: patient.Address?.State || "",
        zipCode: patient.Address?.ZipCode || "",
        phoneNumber:
          patient.CommunicationNumbers?.PrimaryTelephone?.Number || "",
      };
    } catch (err) {
      return null;
    }
  };

  const handleBackToWorkflowSelection = () => {
    // Reset all state
    resetS3();
    resetUPMS();
    setFileName("");
    setJsonInput("");
    setPatientDetails(null);
    setSelectedWorkflow(WorkflowType.NONE);
  };

  const handleWorkflowSelection = (workflow: WorkflowType) => {
    setSelectedWorkflow(workflow);
    setTimeout(() => {
      workflowContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(event.target.value);
  };

  // Advance to the Approve step, carrying the customer identifiers plus the drug
  // identity uploaded in step 1. The Approve step looks up and strictly matches the
  // prescriptionId there (with a Refresh affordance), so ingestion timing does not
  // block this Continue.
  const handleContinueToApprove = () => {
    if (!onContinue) {
      return;
    }
    onContinue(nextStepHandoff());
  };

  const handleContinueToFollowUp = () => {
    onContinueToFollowUp?.(nextStepHandoff());
  };

  const nextStepHandoff = (): INewRxStepHandoff => ({
    fileName,
    personId: handoff?.personId,
    profileId: handoff?.profileId,
    drugDescription: handoff?.drugDescription,
    ndc: handoff?.ndc,
  });

  const handleIngestFromS3 = async () => {
    if (!fileName.trim()) {
      return;
    }

    try {
      // Step 1: Retrieve prescription data from S3
      const data = await retrieveFromS3(fileName);

      // Extract patient details from retrieved data
      const details = extractPatientDetails(data);
      setPatientDetails(details);

      // Small delay to let user see the retrieved data
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Step 2: Submit to UPMS
      await ingestNewRx(data, fileName);

      // Scroll to the post-ingestion next step once it renders.
      setTimeout(scrollToPostIngestion, 500);
    } catch (err) {
      // Error is already handled by the hooks
    }
  };

  // After ingestion, scroll to the relevant next step: the "Continue to Approve"
  // prompt in the chained flow, or the optional Create Customer step standalone.
  const scrollToPostIngestion = () => {
    const target = onContinue ? nextStepRef.current : step2Ref.current;
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const handleIngestFromJSON = async () => {
    if (!jsonInput.trim()) {
      return;
    }

    try {
      const parsedJson = JSON.parse(jsonInput);

      // Extract patient details from JSON
      const details = extractPatientDetails(parsedJson);
      setPatientDetails(details);

      // Ingest to UPMS
      await ingestNewRx(parsedJson, "manual-json-input");

      // Scroll to the post-ingestion next step once it renders.
      setTimeout(scrollToPostIngestion, 500);
    } catch (err) {
      // Error is already handled by the hook
    }
  };

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      {selectedWorkflow === WorkflowType.NONE && (
        <Grid container spacing={4} style={{ maxWidth: 800, marginTop: 20 }}>
          <Grid item xs={12}>
            <WorkflowSelectionHeader
              title="Choose Your Workflow"
              subtitle="Select how you'd like to ingest a NewRx prescription"
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              <Typography
                variant="body2"
                style={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                NewRx Sequential Workflow
              </Typography>
              <Typography variant="body2">
                Step 1: Upload to S3 → Step 2: Ingest to UPMS. Ensure Step 1 is
                completed before using this operation.
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12} md={6}>
            <WorkflowCard
              icon={CloudDownloadIcon}
              title="Retrieve from S3 & ingest to UPMS"
              description="• Enter S3 file name<br />• Retrieve prescription from S3 & Ingest to UPMS<br />• Optionally create AP customer after (Note: May create duplicate profile)"
              onClick={() => handleWorkflowSelection(WorkflowType.S3_RETRIEVAL)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <WorkflowCard
              icon={CodeIcon}
              title="Upload JSON"
              description="• Paste prescription JSON directly<br />• Full control over payload<br />• Ingest to UPMS<br />• No customer creation option"
              onClick={() => handleWorkflowSelection(WorkflowType.UPLOAD_JSON)}
            />
          </Grid>
        </Grid>
      )}

      {selectedWorkflow === WorkflowType.S3_RETRIEVAL && (
        <div ref={workflowContentRef}>
          <Button
            variant="text"
            onClick={handleBackToWorkflowSelection}
            startIcon={<ArrowBackIcon />}
            style={{ color: "black", marginBottom: 16 }}
          >
            Back to Workflow Selection
          </Button>

          <StyledHeaderBox
            title="Ingest NewRx from S3 to UPMS"
            subtitle={`Retrieval source: ${S3_CONFIG.getFullPath()}`}
            awsAccount="pillpack-erx-beta"
            color="#546e7a"
            icon="1"
            infoMessage={
              <>
                This tool automates the steps from the{" "}
                <a
                  href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/HowTos/HowtoIngestNewRxusingUPMS"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.primary }}
                >
                  NewRx Ingestion SOP
                </a>
                . Enter the S3 file name to retrieve and ingest the prescription
                to UPMS (Pharmacy Unified Prescription Message Service). You can
                optionally create an Amazon Pharmacy customer profile after
                ingesting.
              </>
            }
          />

          {/* Input Section */}
          <Paper
            elevation={1}
            style={{
              padding: 32,
              borderRadius: 8,
              backgroundColor: COLORS.white,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="File Name"
                  variant="outlined"
                  value={fileName}
                  onChange={handleFileNameChange}
                  placeholder="e.g., 123456.json"
                  fullWidth
                  helperText="Enter the full file name including extension (e.g., fileName.json)"
                />
              </Grid>

              {(s3Error || upmsError) && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Error:</strong> {s3Error || upmsError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Retrieve from S3 & Ingest to UPMS"
                  loadingLabel="Processing..."
                  onClick={handleIngestFromS3}
                  disabled={!fileName.trim() || !!upmsResponse}
                  loading={isLoading}
                  color={COLORS.orange}
                  startIcon={<SendIcon />}
                />
              </Grid>
            </Grid>
          </Paper>
        </div>
      )}

      {selectedWorkflow === WorkflowType.UPLOAD_JSON && (
        <div ref={workflowContentRef}>
          <Button
            variant="text"
            onClick={handleBackToWorkflowSelection}
            startIcon={<ArrowBackIcon />}
            style={{ color: "black", marginBottom: 16 }}
          >
            Back to Workflow Selection
          </Button>

          <StyledHeaderBox
            title="Ingest NewRx JSON to UPMS"
            subtitle="Direct JSON ingestion to UPMS"
            color="#546e7a"
            icon="1"
            infoMessage={
              <>
                This workflow allows you to paste a NewRx prescription JSON
                payload directly and ingest it to UPMS (Pharmacy Unified
                Prescription Message Service). This is useful for testing custom
                payloads or when you already have the JSON data. Note: This
                workflow does not support customer creation - use the Ingest
                NewRx from S3 to UPMS workflow if you need that functionality.
              </>
            }
          />

          <Paper
            elevation={1}
            style={{
              padding: 32,
              borderRadius: 8,
              backgroundColor: COLORS.white,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  multiline
                  minRows={20}
                  label="NewRx Prescription JSON"
                  variant="filled"
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  fullWidth
                  placeholder='{"MessageType": "NewRx", ...}'
                />
              </Grid>

              {upmsError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>UPMS Ingestion Error:</strong> {upmsError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Ingest JSON to UPMS"
                  loadingLabel="Ingesting..."
                  onClick={handleIngestFromJSON}
                  disabled={!jsonInput.trim() || !!upmsResponse}
                  loading={isLoading}
                  color={COLORS.orange}
                  startIcon={<SendIcon />}
                />
              </Grid>
            </Grid>
          </Paper>
        </div>
      )}

      {isLoading && <LoadingSpinner />}

      {upmsResponse && (
        <>
          <DataAccordion title="Retrieved S3 Data" data={retrievedData} />
          <DataAccordion
            title="Transformed UPMS Payload"
            data={transformedPayload}
          />
          <DataAccordion title="UPMS Ingestion Response" data={upmsResponse} />

          {/* Chained NewRx Operations flow: offer to continue to the Approve step.
              Purely additive — the existing post-ingestion content below is
              unchanged. */}
          {onContinue && (
            <div ref={nextStepRef}>
              <Grid item xs={12} style={{ marginTop: 16 }}>
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    Next Step: Approve Prescription / OTC or Route to Follow-Up
                  </Typography>
                  <Typography variant="body2">
                    Continue to approve the prescription or Rxless OTC for this
                    customer, or send the prescription to follow-up instead.
                  </Typography>
                </Alert>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <StyledActionButton
                    label="Continue to Approve Prescription / OTC"
                    onClick={handleContinueToApprove}
                    color={COLORS.orange}
                    startIcon={<ArrowForwardIcon />}
                  />
                  {onContinueToFollowUp && (
                    <StyledActionButton
                      label="Continue to Route to Follow-Up"
                      onClick={handleContinueToFollowUp}
                      color={COLORS.orange}
                      startIcon={<ArrowForwardIcon />}
                    />
                  )}
                </div>
              </Grid>
            </div>
          )}

          <ResetWorkflowButton
            onReset={() => {
              resetS3();
              resetUPMS();
              setFileName("");
              setJsonInput("");
              setPatientDetails(null);
              setSelectedWorkflow(WorkflowType.NONE);
              // Scroll to top
              setTimeout(() => {
                workflowContentRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }, 100);
            }}
          />

          <div ref={step2Ref}>
            <CustomerCreationSection
              patientDetails={patientDetails || undefined}
              showAcknowledgment={true}
              showCriticalPostIngestionWarning={true}
              stepNumber="1b"
              title="Create Pharmacy Customer (Optional)"
              subtitle="Create a new pharmacy customer using the prescription data"
            />
          </div>

          <ResetWorkflowButton
            onReset={() => {
              resetS3();
              resetUPMS();
              setFileName("");
              setJsonInput("");
              setPatientDetails(null);
              setSelectedWorkflow(WorkflowType.NONE);
              // Scroll to top
              setTimeout(() => {
                workflowContentRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }, 100);
            }}
          />
        </>
      )}
    </Grid>
  );
};

export default IngestNewRxToUPMSSection;
